import { captureException, captureMessage } from "@sentry/gatsby";
import fetch from "cross-fetch";
import ctPromiseRetry from "../promise-retry";
import { authenticate, type CommercetoolsSession } from "./auth-service";

export interface State<T> {
	data?: T;
	session?: CommercetoolsSession;
	error?: CommercetoolsResponseErrorInterface;
}

interface CommercetoolsHttpResponseExceptionInterface {
	code: string;
	message: string;
	currentVersion?: number;
}

interface CommercetoolsResponseErrorInterface extends Error {
	statusCode: number;
	errors: CommercetoolsHttpResponseExceptionInterface[];
}

class CommercetoolsHttpResponseException extends Error {
	statusCode: number;
	errors: CommercetoolsHttpResponseExceptionInterface[];
	constructor(response: CommercetoolsResponseErrorInterface) {
		super();

		this.name = "CommercetoolsHttpResponseException";
		this.statusCode = response.statusCode;
		this.message = response.message;
		this.errors = response.errors;
		this.stack = new Error(response.message).stack;
	}
}

export const isCommercetoolsHttpResponseError = (
	err: unknown,
): err is CommercetoolsResponseErrorInterface => {
	if (
		(err as CommercetoolsResponseErrorInterface).statusCode &&
		(err as CommercetoolsResponseErrorInterface).message &&
		Array.isArray((err as CommercetoolsResponseErrorInterface).errors)
	) {
		return true;
	}

	return false;
};

export const fetchFromCT = async <T = unknown>(
	path: string,
	session?: CommercetoolsSession,
	body?: Record<string, unknown>,
	method?: "GET" | "POST" | "DELETE",
): Promise<State<T>> => {
	const performRequest = async () => {
		const now = Math.floor(Date.now() / 1000);
		if (!session || session.expires < now) {
			session = await authenticate(session);
		}

		return fetch(new URL(path, process.env.GATSBY_COMMERCETOOLS_API_URL).href, {
			headers: {
				Authorization: `Bearer ${session.accessToken}`,
				"Content-Type": "application/json",
			},
			method: method ?? (body ? "POST" : "GET"),
			body: body ? JSON.stringify(body) : undefined,
		});
	};

	try {
		let response = await performRequest();
		switch (response.status) {
			case 401:
			case 403: {
				session = await authenticate(session);
				response = await performRequest();

				break;
			}
			case 409: {
				captureMessage("Version mismatch", {
					level: "warning",
					tags: {
						error_type: "commercetoolsVersionMismatch",
					},
					extra: {
						Response: body,
					},
				});

				break;
			}
			case 504: {
				response = await ctPromiseRetry(performRequest);

				break;
			}
			default: {
				if (!response.ok) {
					const json =
						(await response.json()) as CommercetoolsResponseErrorInterface;
					const error = new CommercetoolsHttpResponseException(json);
					captureException(error, {
						tags: {
							error_type: "commercetoolsHttpResponseException",
						},
						extra: {
							Response: JSON.stringify(json),
						},
					});

					return { error, session };
				}
			}
		}

		// TODO: Add type guard
		const json = (await response.json()) as T | undefined;

		return { session, data: json };
	} catch (error) {
		captureException(error, {
			tags: {
				error_type: "commercetoolsHttpResponseException",
			},
		});

		return { session, error: error as CommercetoolsResponseErrorInterface };
	}
};
